import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock } from "lucide-react";
import { Eyebrow, setHeadMeta } from "@/components/kit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

export default function BlogArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    setArticle(null);
    axios.get(`${API}/articles/${slug}`)
      .then((r) => { setArticle(r.data); setStatus("ok"); })
      .catch((e) => setStatus(e?.response?.status === 404 ? "notfound" : "error"));
  }, [slug]);

  useEffect(() => {
    if (status === "notfound" || status === "error") {
      document.title = "Article Not Found | Redeemer Solutions LLC";
      setHeadMeta({ robots: "noindex" });
      return;
    }
    if (status !== "ok" || !article) return;
    const origin = window.location.origin;
    const url = `${origin}/blog/${article.slug}`;
    const title = article.meta_title || article.title;
    const desc = article.meta_description || article.subtitle || "";
    document.title = title;
    setHeadMeta({
      description: desc,
      canonical: url,
      og: { type: "article", title, description: desc, image: article.image_url, url },
    });
    let ld = null;
    if (article.json_ld_schema) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.setAttribute("data-testid", "blog-article-jsonld");
      ld.textContent = JSON.stringify(article.json_ld_schema);
      document.head.appendChild(ld);
    }
    return () => { if (ld) ld.remove(); };
  }, [status, article]);

  if (status === "loading") {
    return <div className="min-h-[60vh] pt-44 px-5 text-zinc-500 text-sm" data-testid="blog-article-loading">Loading article…</div>;
  }

  if (status === "notfound" || status === "error") {
    return (
      <div data-testid="blog-article-notfound" className="min-h-[70vh] flex flex-col items-center justify-center text-center px-5 pt-32 pb-24">
        <p className="font-display text-6xl text-gold mb-4">404</p>
        <h1 className="font-display text-2xl text-[#F4EFE6] mb-3">This article isn't here anymore.</h1>
        <p className="text-zinc-400 text-sm mb-8">It may have been unpublished or moved.</p>
        <Link to="/blog" data-testid="blog-back-to-list" className="inline-flex items-center gap-2 border border-gold/40 text-gold-champagne text-sm px-7 py-3.5 rounded-sm transition-colors hover:bg-gold/10">
          <ArrowLeft size={15} /> Back to the Blog
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-article-page">
      <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-40 pb-24">
        <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-gold-champagne transition-colors mb-10">
          <ArrowLeft size={15} /> Back to the Blog
        </Link>
        {article.category && <Eyebrow className="mb-4">{article.category}</Eyebrow>}
        <h1 className="font-display text-4xl sm:text-5xl font-semibold text-[#F4EFE6] leading-tight" data-testid="blog-article-title">{article.title}</h1>
        {article.subtitle && <p className="text-lg text-zinc-400 mt-4 leading-relaxed">{article.subtitle}</p>}
        <p className="flex flex-wrap items-center gap-3 text-xs text-zinc-500 mt-6 pb-8 border-b border-gold/15">
          {article.author_name && <span className="text-gold-champagne">{article.author_name}</span>}
          {article.published_at && <span>{fmtDate(article.published_at)}</span>}
          {article.read_time && <span className="inline-flex items-center gap-1"><Clock size={12} /> {article.read_time}</span>}
        </p>
        {article.image_url && <img src={article.image_url} alt={article.title} className="w-full rounded-sm border border-gold/15 my-10" />}
        <div className="article-content mt-4" data-testid="blog-article-content" dangerouslySetInnerHTML={{ __html: article.content_html || "" }} />
        {Array.isArray(article.faqs) && article.faqs.length > 0 && (
          <div className="mt-14 border-t border-gold/15 pt-10" data-testid="blog-article-faqs">
            <Eyebrow className="mb-6">Common Questions</Eyebrow>
            {article.faqs.map((f, i) => (
              <div key={i} className="mb-7">
                <h3 className="font-display text-lg text-[#F4EFE6] mb-2">{f.question || f.q}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{f.answer || f.a}</p>
              </div>
            ))}
          </div>
        )}
      </article>
    </div>
  );
}
